import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { Tenant } from '../types';
import { fetchTenantsList } from '../services/api';

interface SuperAdminTenantContextType {
  tenantId: number | null;
  tenant: Tenant | null;
  tenants: Tenant[];
  setTenantId: (id: number | null) => void;
}

const SuperAdminTenantContext = createContext<SuperAdminTenantContextType>({
  tenantId: null, tenant: null, tenants: [], setTenantId: () => {},
});

export const SuperAdminTenantProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem('super_tenant_id');
    return saved ? Number(saved) : null;
  });

  const tenant = tenants.find(t => t.id === tenantId) ?? null;

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchTenantsList()
      .then(list => {
        const active = list.filter((t: Tenant) => t.is_active);
        setTenants(active);
        if (tenantId !== null && !active.find((t: Tenant) => t.id === tenantId)) {
          const first = active[0]?.id ?? null;
          setTenantIdState(first);
          if (first) localStorage.setItem('super_tenant_id', String(first));
          else localStorage.removeItem('super_tenant_id');
        }
      })
      .catch(() => {});
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTenantId = (id: number | null) => {
    setTenantIdState(id);
    if (id) localStorage.setItem('super_tenant_id', String(id));
    else localStorage.removeItem('super_tenant_id');
  };

  return (
    <SuperAdminTenantContext.Provider value={{ tenantId, tenant, tenants, setTenantId }}>
      {children}
    </SuperAdminTenantContext.Provider>
  );
};

export const useSuperTenant = () => useContext(SuperAdminTenantContext);
